package com.patronat.top.ui.molecules.inputs

import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.patronat.top.ui.atoms.inputs.InputLabel
import com.patronat.top.ui.atoms.inputs.InputMessage
import com.patronat.top.ui.atoms.inputs.InputMessageType
import com.patronat.top.ui.atoms.inputs.InputState
import com.patronat.top.ui.atoms.inputs.InputStyles
import com.patronat.top.ui.atoms.inputs.OtpCharBox
import com.patronat.top.ui.theme.*
import org.jetbrains.compose.resources.stringResource
import top.design_system.generated.resources.Res
import top.design_system.generated.resources.otp_label
import top.design_system.generated.resources.otp_resend
import top.design_system.generated.resources.otp_timer_prefix

@Composable
fun AppOtpInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    length: Int = 6,
    timerText: String = "00:00",
    onResendClick: () -> Unit = {},
    errorText: String? = null,
    forceState: InputState? = null
) {
    val interactionSource = remember { MutableInteractionSource() }

    Column(modifier = modifier.width(IntrinsicSize.Min)) {

        // Label
        InputLabel(text = stringResource(Res.string.otp_label))

        Spacer(modifier = Modifier.height(12.dp))

        BasicTextField(
            value = value,
            onValueChange = {
                if (it.length <= length && it.all { char -> char.isDigit() }) {
                    onValueChange(it)
                }
            },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.NumberPassword),
            interactionSource = interactionSource,
            cursorBrush = SolidColor(Color.Transparent),
            textStyle = MaterialTheme.typography.bodyLarge.copy(color = Color.Transparent),
            decorationBox = {
                Row(
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.wrapContentSize()
                ) {
                    repeat(length) { index ->
                        val char = value.getOrNull(index)?.toString() ?: ""
                        val isFocused = value.length == index

                        val cellState = when {
                            errorText != null || forceState == InputState.Error -> InputState.Error
                            forceState == InputState.Success -> InputState.Success
                            isFocused -> InputState.Active
                            char.isNotEmpty() -> InputState.Filled
                            else -> InputState.Default
                        }

                        OtpCharBox(
                            char = char,
                            styles = getOtpInputStyles(cellState)
                        )

                        if (index < length - 1) {
                            val spacerWidth = if (index == length / 2 - 1) 16 else 8
                            Spacer(modifier = Modifier.width(spacerWidth.dp))
                        }
                    }
                }
            }
        )

        if (errorText != null) {
            Spacer(modifier = Modifier.height(8.dp))
            InputMessage(text = errorText, type = InputMessageType.Error)
        }

        Spacer(modifier = Modifier.height(16.dp))

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "${stringResource(Res.string.otp_timer_prefix)} $timerText",
                style = MaterialTheme.typography.bodyMedium.copy(color = MineShaft600)
            )


            Text(
                text = stringResource(Res.string.otp_resend),
                style = MaterialTheme.typography.bodyMedium.copy(
                    color = MineShaft900,
                    fontWeight = FontWeight.Bold
                ),
                modifier = Modifier.clickable { onResendClick() }
            )
        }
    }
}
@Composable
private fun getOtpInputStyles(state: InputState): InputStyles {
    val baseTextStyle = MaterialTheme.typography.titleLarge

    return when (state) {
        InputState.Default -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.Normal, color = MineShaft500),
            placeholderColor = Color.Transparent,
            iconColor = Color.Transparent
        )
        InputState.Active -> InputStyles(
            borderColor = Primary600,
            borderWidth = 2.dp,
            backgroundColor = Primary100,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft900),
            placeholderColor = Color.Transparent,
            iconColor = Color.Transparent
        )
        InputState.Filled -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft900),
            placeholderColor = Color.Transparent,
            iconColor = Color.Transparent
        )
        InputState.Error -> InputStyles(
            borderColor = AccentPink,
            borderWidth = 2.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft900),
            placeholderColor = Color.Transparent,
            iconColor = AccentPink
        )
        else -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle,
            placeholderColor = Color.Transparent,
            iconColor = Color.Transparent
        )
    }
}